// Kompas Dnia — insight/analytics helpers.
// Pure functions on top of the store. No side effects, easy to test.

import { getCheckInByDate } from "@/store/store";
import { todayLocalISO } from "@/lib/date";

/**
 * Returns an array of 7 slots (oldest → today) with the entry for each day.
 *
 *   [
 *     { date: '2026-01-26', entry: null | CheckInEntry },
 *     ...
 *     { date: '2026-02-01', entry: ... },  // today
 *   ]
 */
export function getLast7Days() {
  const today = new Date();
  const slots = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - i,
    );
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const iso = `${y}-${m}-${day}`;
    slots.push({ date: iso, dayIndex: d.getDay(), entry: getCheckInByDate(iso) });
  }
  return slots;
}

/**
 * Tie-break priority for the "most common result" in the weekly summary.
 * We prefer calmer results first, per spec.
 */
const RESULT_PRIORITY = ["stay", "act", "rest", "change", "pause"];

/**
 * Weekly summary over the last 7 days.
 * Returns null if fewer than 3 check-ins exist in that window.
 */
export function getWeeklySummary() {
  const entries = getLast7Days()
    .map((s) => s.entry)
    .filter(Boolean);

  if (entries.length < 3) return null;

  const counts = { act: 0, rest: 0, stay: 0, change: 0, pause: 0 };
  let sumBalance = 0;
  for (const e of entries) {
    if (counts[e.result] !== undefined) counts[e.result] += 1;
    sumBalance += Number(e.balanceScore) || 0;
  }

  const maxCount = Math.max(...Object.values(counts));
  const mostCommonResult =
    RESULT_PRIORITY.find((r) => counts[r] === maxCount) || "stay";

  return {
    mostCommonResult,
    resultCounts: counts,
    averageBalance: Math.round(sumBalance / entries.length),
    totalCheckIns: entries.length,
  };
}

/**
 * Convenience: does today have a saved check-in?
 */
export function hasTodayCheckIn() {
  return !!getCheckInByDate(todayLocalISO());
}
